import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subscriber } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

const AI_SESSION_STORAGE_KEY = 'ai_chat_session_id';
const AI_SESSION_HAS_CONVERSATION_KEY = 'ai_chat_has_conversation';

export interface AiChatMessageData {
    message: string;
    provider?: 'gpt' | 'perplexity' | 'fallback';
    sources?: Array<{ title?: string; chunk?: string;[key: string]: unknown }>;
    suggestions?: string[];
    tokens_used?: number;
    response_time_ms?: number;
}

export interface AiChatResponse {
    success: boolean;
    data?: AiChatMessageData;
    error?: string;
}

export interface AiChatMessage {
    id?: number;
    role: 'user' | 'assistant';
    content: string;
    llm_provider?: string;
    document_references?: unknown;
    created_at?: string;
}

export interface AiConversationResponse {
    success: boolean;
    data?: {
        conversation: { id: number; session_id: string;[key: string]: unknown };
        messages: AiChatMessage[];
    };
    error?: string;
}

export interface AiConversationsListResponse {
    success: boolean;
    data?: { conversations: Array<{ id: number; session_id: string; messages_count?: number; latest_message?: string;[key: string]: unknown }> };
    error?: string;
}

export type AiChatStreamEventKind = 'delta' | 'final' | 'done' | 'error' | 'start';

export interface AiChatStreamEvent {
    kind: AiChatStreamEventKind;
    text?: string;
    data?: AiChatMessageData;
    error?: string;
}

@Injectable({ providedIn: 'root' })
export class AiChatService {
    private readonly apiUrl = environment.apiUrl;

    constructor(private readonly http: HttpClient) { }

    private static generateSessionId(): string {
        if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
            return window.crypto.randomUUID();
        }
        // Fallback for older browsers
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    /**
     * Get or create a stable session ID for this tab.
     * Guests and employees both use this; same session_id = same thread on the backend.
     */
    getOrCreateSessionId(): string {
        let id = sessionStorage.getItem(AI_SESSION_STORAGE_KEY);
        if (!id) {
            id = AiChatService.generateSessionId();
            sessionStorage.setItem(AI_SESSION_STORAGE_KEY, id);
        }
        return id;
    }

    /**
     * Whether this tab already has a known conversation persisted on the backend.
     * Prevents a 404 "not found" fetch for brand-new sessions.
     */
    hasKnownConversation(): boolean {
        return sessionStorage.getItem(AI_SESSION_HAS_CONVERSATION_KEY) === '1';
    }

    /**
     * Mark that the current session id has a conversation on the backend.
     */
    markConversationKnown(): void {
        sessionStorage.setItem(AI_SESSION_HAS_CONVERSATION_KEY, '1');
    }

  /**
   * Send a message to the AI chat API.
   * Guest: no JWT (interceptor won't add one). Employee: JWT added by interceptor.
   * @param message User message (max 4000 chars)
   * @param customerId Optional; for employees, current customer id
   * @param locale Optional; e.g. 'en' or 'sv' so the backend can respond in that language
   */
    /**
     * Regenerate follow-up suggestion chips for the current session in the given locale.
     * Implement this on the backend: load conversation context, call OpenAI (or your model), return new `suggestions`.
     * The browser must not call OpenAI directly (no API key in the client).
     */
    regenerateSuggestions(locale: string, customerId?: number | null): Observable<AiChatResponse> {
        const sessionId = this.getOrCreateSessionId();
        const body: { session_id: string; locale: string; customer_id?: number } = {
            session_id: sessionId,
            locale
        };
        if (customerId != null && customerId > 0) {
            body.customer_id = customerId;
        }
        return this.http
            .post<AiChatResponse>(`${this.apiUrl}/ai/chat/suggestions`, body)
            .pipe(
                catchError(() =>
                    of({
                        success: false,
                        error: 'Failed to regenerate suggestions'
                    })
                )
            );
    }

    sendMessage(message: string, customerId?: number | null, locale?: string | null): Observable<AiChatResponse> {
        const sessionId = this.getOrCreateSessionId();
        const body: { message: string; session_id: string; customer_id?: number; locale?: string; include_suggestions?: boolean } = {
            message: message.trim(),
            session_id: sessionId,
            include_suggestions: true
        };
        if (customerId != null && customerId > 0) {
            body.customer_id = customerId;
        }
        if (locale) {
            body.locale = locale;
        }
        return this.http
            .post<AiChatResponse>(`${this.apiUrl}/ai/chat`, body)
            .pipe(
                map((res) => {
                    if (res?.success) {
                        this.markConversationKnown();
                    }
                    return res;
                }),
                catchError((err) => of({
                    success: false,
                    error: err?.error?.error || err?.message || 'Failed to send message'
                }))
            );
    }

    streamMessage(message: string, customerId?: number | null, locale?: string | null): Observable<AiChatStreamEvent> {
        const sessionId = this.getOrCreateSessionId();
        const payload: { message: string; session_id: string; stream: true; customer_id?: number; locale?: string; include_suggestions?: boolean } = {
            message: message.trim(),
            session_id: sessionId,
            stream: true,
            include_suggestions: true
        };
        if (customerId != null && customerId > 0) {
            payload.customer_id = customerId;
        }
        if (locale) {
            payload.locale = locale;
        }
        const headers: Record<string, string> = {
            'Content-Type': 'application/json'
        };
        const jwt = typeof localStorage !== 'undefined' ? localStorage.getItem('jwt') : null;
        if (jwt) {
            headers.Authorization = `Bearer ${jwt}`;
        }

        return new Observable<AiChatStreamEvent>((subscriber) => {
            if (typeof fetch !== 'function') {
                subscriber.error(new Error('Streaming fetch is not available in this browser'));
                return;
            }

            const controller = new AbortController();

            fetch(`${this.apiUrl}/ai/chat`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
                signal: controller.signal
            }).then((response) => {
                if (!response.ok) {
                    response.text().then((bodyText) => {
                        subscriber.error(new Error(bodyText || response.statusText));
                    }).catch((err) => subscriber.error(err));
                    return;
                }

                const reader = response.body?.getReader();
                if (!reader) {
                    response.text().then((text) => {
                        AiChatService.handleStreamLine(text, subscriber);
                        subscriber.complete();
                    }).catch(err => subscriber.error(err));
                    return;
                }

                const decoder = new TextDecoder();
                let buffered = '';

                const readNext = (): void => {
                    reader.read().then(({ done, value }) => {
                        if (done) {
                            if (buffered.trim()) {
                                AiChatService.handleStreamLine(buffered, subscriber);
                            }
                            subscriber.complete();
                            return;
                        }
                        buffered += decoder.decode(value || new Uint8Array(), { stream: true });
                        let newlineIndex: number;
                        while ((newlineIndex = buffered.indexOf('\n')) >= 0) {
                            const line = buffered.slice(0, newlineIndex);
                            buffered = buffered.slice(newlineIndex + 1);
                            AiChatService.handleStreamLine(line, subscriber);
                        }
                        readNext();
                    }).catch((err) => {
                        if ((err as Error)?.name === 'AbortError') {
                            subscriber.complete();
                            return;
                        }
                        subscriber.error(err);
                    });
                };

                readNext();
            }).catch((err) => {
                if ((err as Error)?.name === 'AbortError') {
                    subscriber.complete();
                    return;
                }
                subscriber.error(err);
            });

            subscriber.add(() => controller.abort());
        });
    }

    private static handleStreamLine(line: string, subscriber: Subscriber<AiChatStreamEvent>): void {
        const trimmed = line.trim();
        if (!trimmed) {
            return;
        }

        if (trimmed === '[DONE]' || trimmed === 'data: [DONE]') {
            subscriber.next({ kind: 'done' });
            return;
        }

        // SSE: only parse payload lines; ignore event:/id:/retry: so we don't treat "event: chunk" as text.
        if (trimmed.startsWith('event:') || trimmed.startsWith('id:') || trimmed.startsWith('retry:')) {
            return;
        }

        let payloadText: string;
        if (trimmed.startsWith('data:')) {
            payloadText = trimmed.slice('data:'.length).trim();
        } else if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            payloadText = trimmed;
        } else {
            return;
        }

        if (!payloadText) {
            return;
        }

        try {
            const parsed = JSON.parse(payloadText);
            const event = AiChatService.parseStreamPayload(parsed);
            if (event) {
                subscriber.next(event);
            }
        } catch {
            const event = AiChatService.parseStreamPayload(payloadText);
            if (event) {
                subscriber.next(event);
            }
        }
    }

    private static parseStreamPayload(payload: unknown): AiChatStreamEvent | null {
        if (payload === null || payload === undefined) {
            return null;
        }

        if (typeof payload === 'string') {
            return payload ? { kind: 'delta', text: payload } : null;
        }

        if (!AiChatService.isRecord(payload)) {
            return null;
        }

        if (payload.error) {
            return { kind: 'error', error: AiChatService.toText(payload.error) };
        }

        if (payload.success === false) {
            const fallback = payload.error ?? 'Request failed';
            return { kind: 'error', error: AiChatService.toText(fallback) };
        }

        const data = payload.data;
        if (AiChatService.isRecord(data)) {
            const aiData = data as unknown as AiChatMessageData;
            if (aiData.message || aiData.provider || aiData.sources || aiData.suggestions) {
                return { kind: 'final', text: aiData.message, data: aiData };
            }
        }

        const openAiEvent = AiChatService.extractOpenAiStreamEvent(payload);
        if (openAiEvent) {
            return openAiEvent;
        }

        // Chunk stream uses `text`; final SSE frame uses `message` + metadata (provider, sources, …).
        if (typeof payload.text === 'string') {
            return { kind: 'delta', text: payload.text };
        }

        if (typeof payload.message === 'string') {
            const isFinalAssistantPayload =
                payload.provider !== undefined ||
                Array.isArray(payload.sources) ||
                Array.isArray(payload.suggestions) ||
                typeof payload.tokens_used === 'number' ||
                typeof payload.response_time_ms === 'number';
            if (isFinalAssistantPayload) {
                const aiData = payload as unknown as AiChatMessageData;
                return { kind: 'final', text: aiData.message, data: aiData };
            }
            return { kind: 'delta', text: payload.message };
        }

        if (typeof payload.delta === 'string') {
            return { kind: 'delta', text: payload.delta };
        }

        // SSE `event: start` — server accepted the request; UI can move from "Thinking" to "Generating".
        if (
            (typeof payload.conversation_id === 'number' || typeof payload.conversation_id === 'string') &&
            typeof payload.text !== 'string' &&
            typeof payload.message !== 'string'
        ) {
            return { kind: 'start' };
        }

        return null;
    }

    private static extractOpenAiStreamEvent(payload: Record<string, unknown>): AiChatStreamEvent | null {
        const choices = Array.isArray(payload.choices) ? payload.choices : [];
        if (!choices.length) {
            return null;
        }
        const first = choices[0];
        if (!AiChatService.isRecord(first)) {
            return null;
        }

        const delta = first.delta;
        if (AiChatService.isRecord(delta)) {
            const content = delta.content;
            if (typeof content === 'string' && content) {
                return { kind: 'delta', text: content };
            }
        } else if (typeof delta === 'string' && delta) {
            return { kind: 'delta', text: delta };
        }

        if (AiChatService.isRecord(first.message)) {
            const content = (first.message as Record<string, unknown>).content;
            if (typeof content === 'string' && content) {
                return { kind: 'final', text: content };
            }
        }

        return null;
    }

    private static toText(value: unknown): string {
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number' || typeof value === 'boolean') {
            return String(value);
        }
        try {
            return JSON.stringify(value);
        } catch {
            return '';
        }
    }

    private static isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    /**
     * Get one conversation and its messages by session ID.
     */
    getConversation(sessionId: string): Observable<AiConversationResponse> {
        return this.http
            .get<AiConversationResponse>(`${this.apiUrl}/ai/conversations/${encodeURIComponent(sessionId)}`)
            .pipe(
                catchError((err) => of({
                    success: false,
                    error: err?.error?.error || err?.message || 'Failed to load conversation'
                }))
            );
    }

    /**
     * Delete one conversation and all its messages.
     */
    deleteConversation(sessionId: string): Observable<{ success: boolean; error?: string }> {
        return this.http
            .delete<{ success: boolean; data?: { message: string }; error?: string }>(
                `${this.apiUrl}/ai/conversations/${encodeURIComponent(sessionId)}`
            )
            .pipe(
                map((res) => (res.success !== false ? { success: true } : { success: false, error: res.error })),
                catchError((err) => of({
                    success: false,
                    error: err?.error?.error || err?.message || 'Failed to delete conversation'
                }))
            );
    }

    /**
     * List conversations for the current employee (requires JWT).
     */
    listConversations(): Observable<AiConversationsListResponse> {
        return this.http
            .get<AiConversationsListResponse>(`${this.apiUrl}/ai/conversations`)
            .pipe(
                catchError((err) => of({
                    success: false,
                    error: err?.error?.error || err?.message || 'Failed to list conversations'
                }))
            );
    }
}
